class CartDiscountInput extends HTMLElement {
  constructor() {
    super();
  }
  // Authoritative cart read → the single trigger for reason fetches. Guarded so rapid cart events
  // (add + updated) don't fire parallel reads.
  getCart() {
    if(this._getCartInFlight) return;
    this._getCartInFlight = true;
    fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'Get',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(response => response.json())
    .then(response => {
      if(response.discount_codes) { this.discount_codes = response.discount_codes; }
      this.cart_items = response.items || [];
      this.captureCartTotals(response);
      this.init();
    })
    .catch((error) => { console.error('Error:', error); })
    .finally(() => { this._getCartInFlight = false; });
  }
  // remember the subtotal (for the Mbw threshold) + currency from a cart payload
  captureCartTotals(cart) {
    if(!cart) return;
    if(typeof cart.items_subtotal_price === 'number') { this.cartSubtotal = cart.items_subtotal_price; }
    else if(typeof cart.total_price === 'number') { this.cartSubtotal = cart.total_price; }
    if(cart.currency) { this.cartCurrency = cart.currency; }
    // qty/line/applicability changes alter which reason applies → drop stale cart-dependent reasons
    const sig = this.cartSubtotal
      + '|' + (this.cart_items || []).map(i => i.variant_id + ':' + i.quantity).join(',')
      + '|' + (this.discount_codes || []).map(dc => (dc.code || '') + ':' + (dc.applicable ? 1 : 0)).join(',');
    if(this._cartSig !== undefined && this._cartSig !== sig) { this.invalidateStaleReasons(); }
    this._cartSig = sig;
  }
  // Reasons are cached per code, but most depend on the cart (subtotal/quantities/products).
  // On a cart change, keep only what is definition-based — everything else is deleted so the next
  // init() refetches it (cheap: the middleware serves definitions from its Redis cache):
  //  - not_found / inactive / unknown    → definition/time-based, never stale
  //  - min_not_met while still below the → threshold is definition-based; the remaining amount
  //    known threshold                     is recomputed live anyway (no flicker for the Mbw line)
  //  - applicable while the code applies → definition fact as long as the cart confirms it
  //  - free_shipping only while the KNOWN threshold is still met — Shopify never re-evaluates FS
  //    minimums in the cart (`applicable` stays true below them, verified live), so the cart flag
  //    can't be the trigger here; without a known threshold we must refetch.
  invalidateStaleReasons() {
    const applied = {};
    for(const dc of this.discount_codes) {
      if(dc.applicable && dc.code) { applied[dc.code.toUpperCase()] = true; }
    }
    for(const code of Object.keys(this.reasonByCode)) {
      const reason = this.reasonByCode[code];
      if(reason === 'not_found' || reason === 'inactive' || reason === 'unknown') continue;
      const min = this.minAmountByCode[code];
      if(reason === 'min_not_met') {
        if(min != null && (this.cartSubtotal || 0) < min) continue;
      } else if(reason === 'applicable' && applied[code]) {
        continue;
      } else if(reason === 'free_shipping' && applied[code]) {
        if(min != null && (this.cartSubtotal || 0) >= min) continue;
      }
      delete this.reasonByCode[code];
    }
  }
  removeDiscountCode(code) {
    if(!code) return;
    this.discount_codes = this.discount_codes.filter(dc => dc.code.toUpperCase() !== code);
    window.cart.setDiscount(this.discount_codes.map(dc => dc.code).join(','));
  }
  submitDiscounts() {
    if(!this.input || !window.cart) return;
    const value = this.input.value?.trim()?.toUpperCase();
    this.input.value = '';
    if(!value) return;
    // debounce rapid repeat submits (also limits brute-force / probe spam)
    const now = Date.now();
    if(this._lastSubmit && (now - this._lastSubmit) < 1000) { return; }
    this._lastSubmit = now;
    this.pendingCode = value;
    this.clearError();
    this.setButtonLoading(true);
    // Carry forward applicable codes + non-applicable ones that aren't confirmed typos, + the new
    // one. Confirmed-invalid codes (not_found) are dropped so typos can't pile up.
    const seen = {};
    const codes_to_apply = [];
    this.discount_codes
      .filter(dc => dc.applicable || this.reasonByCode[(dc.code || '').toUpperCase()] !== 'not_found')
      .map(dc => dc.code).filter(Boolean)
      .concat([value])
      .forEach(code => { const k = code.toUpperCase(); if(!seen[k]) { seen[k] = true; codes_to_apply.push(code); } });
    clearTimeout(this._stripTimer);   // don't let a debounced strip race this write (→ 409)
    this._stripping = true;           // cart write in flight; cleared on theme:cart:updated
    window.cart.setDiscount(codes_to_apply.join(','));
  }
  // Transient inline error under the input — used only for a just-entered invalid (typo) code.
  showError(code, tpl, withHint) {
    if(!this.error_el) return;
    const template = tpl || this.dataset.msgInvalid || '[code]';
    const showHint = withHint !== false;
    const tplHint = this.dataset.msgInvalidHint || '';
    this.error_el.innerHTML = '';
    const icon = document.createElement('span');
    icon.classList.add('cart-discount-error__icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'ⓘ';
    const body = document.createElement('span');
    body.classList.add('cart-discount-error__body');
    this._fillTemplate(body, template, code, 'cart-discount-error__code');
    if(showHint && tplHint) {
      const hint = document.createElement('span');
      hint.classList.add('cart-discount-error__hint');
      hint.textContent = tplHint;
      body.appendChild(hint);
    }
    this.error_el.append(icon, body);
    this.error_el.removeAttribute('hidden');
    this.errorCode = code;
  }
  clearError() {
    this.errorCode = null;
    if(!this.error_el) return;
    this.error_el.innerHTML = '';
    this.error_el.setAttribute('hidden', '');
  }
  // loading state on the apply button while a discount request is in flight
  setButtonLoading(on) {
    if(!this.button) return;
    this.button.classList.toggle('is-loading', !!on);
    this.button.setAttribute('aria-busy', on ? 'true' : 'false');
    this.button.disabled = !!on;
    clearTimeout(this._loadingTimer);
    if(on) { this._loadingTimer = setTimeout(() => this.setButtonLoading(false), 8000); }
  }
  // Middleware reason → how to treat / message template.
  reasonInfo(reason) {
    switch(reason) {
      case 'min_not_met': return { category: 'condition', tpl: this.dataset.msgMin };
      case 'needs_product': return { category: 'condition', tpl: this.dataset.msgNeedsProduct };
      case 'quantity': return { category: 'condition', tpl: this.dataset.msgQuantity };
      case 'not_found': return { category: 'typo', tpl: this.dataset.msgInvalid };
      default: return { category: 'other', tpl: this.dataset.msgNotApplicable };
    }
  }
  // Ask the middleware (Admin API, via the App Proxy) WHY `code` isn't applicable (once per code).
  // The Admin lookup is not channel-scoped, so it sees off-channel cart items the Storefront probe
  // could not, and returns the exact minimum-order threshold for the "Noch X €" line.
  fetchReason(code) {
    if(this.reasonByCode[code] !== undefined || this._probing[code]) return; // classified or in flight
    this._probing[code] = true;
    const items = this.cart_items || [];
    const body = {
      code: code,
      subtotal: this.cartSubtotal || 0,
      currency: this.cartCurrency || undefined,
      product_ids: items.map(i => i.product_id).filter(Boolean),
      variant_ids: items.map(i => i.variant_id).filter(Boolean),
      quantities: items.map(i => i.quantity || 0)
    };
    fetch('/apps/app-proxy/discount-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    .then(r => r.ok ? r.json() : Promise.reject(r.status))
    .then(data => this.applyReason(code, data || {}))
    .catch(() => this.applyReason(code, { reason: 'unknown' })); // fail soft → generic line
  }
  // Apply a middleware reason result for `code`, then re-render.
  applyReason(code, data) {
    delete this._probing[code];
    const reason = data.reason || 'unknown';
    this.reasonByCode[code] = reason;
    if(data.minimumAmount != null) {
      this.minAmountByCode[code] = Math.round(parseFloat(data.minimumAmount) * 100); // threshold in cents
      if(data.currency && !this.cartCurrency) { this.cartCurrency = data.currency; }
    }
    // remember which codes are free-shipping discounts (definition fact; also flagged on the
    // min_not_met result so a fresh page load below the threshold knows the code type)
    if(reason === 'free_shipping' || data.freeShipping) { this.fsByCode[code] = true; }
    if(data.message) { this.messageByCode[code] = data.message; } // optional middleware explainer
    const info = this.reasonInfo(reason);
    if(info.category === 'typo' && code === this.pendingCode) {
      this.showError(code, this.dataset.msgInvalid, true); // transient (also stripped by init())
    }
    if(code === this.pendingCode) { this.pendingCode = null; }
    this.render(); // the code just resolved — re-render; don't kick off another fetch sweep
  }
  // Format integer cents as money in the cart currency / page locale.
  _fmtMoney(cents) {
    const cur = this.cartCurrency || (window.theme && window.theme.current_iso_code) || 'EUR';
    const loc = document.documentElement.lang || (window.Shopify && window.Shopify.locale) || 'de';
    try {
      return new Intl.NumberFormat(loc, { style: 'currency', currency: cur }).format(cents / 100);
    } catch(e) {
      return (cents / 100).toFixed(2) + ' ' + cur;
    }
  }
  // Format cents like the free-shipping bar does (theme.moneyFormat + theme.js's ",00" strip), so
  // our injected bar amount is indistinguishable from the theme-rendered one.
  _fmtMoneyLikeBar(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '';
    const m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if(!m) { return this._fmtMoney(cents); }
    const value = cents / 100;
    let amount;
    switch(m[1]) {
      case 'amount_with_comma_separator': amount = value.toFixed(2).replace('.', ','); break;
      case 'amount_no_decimals': amount = String(Math.round(value)); break;
      case 'amount_no_decimals_with_comma_separator': amount = String(Math.round(value)); break;
      default: amount = value.toFixed(2); // 'amount'
    }
    let out = format.replace(m[0], amount).replace(/<[^>]*>/g, '').trim();
    if(out.endsWith('.00') || out.endsWith(',00')) { out = out.slice(0, -3); }
    if(window.theme && window.theme.settings && window.theme.settings.currency_code_enable) {
      out += ' ' + (window.theme.current_iso_code || '');
    }
    return out;
  }
  // Resolve the message template for a non-applicable reason. For min_not_met the remaining amount
  // is recomputed against the *current* subtotal on every render, so it stays correct after qty changes.
  conditionTemplate(code, reason) {
    if(reason === 'min_not_met') {
      const minCents = this.minAmountByCode[code];
      // FS codes get the free-shipping wording ("Noch X € bis zum GRATIS Versand")
      const tplRemaining = (this.fsByCode[code] && this.dataset.msgFsMinRemaining) || this.dataset.msgMinRemaining;
      if(minCents != null && tplRemaining) {
        const remaining = minCents - (this.cartSubtotal || 0);
        if(remaining > 0) { return tplRemaining.split('[amount]').join(this._fmtMoney(remaining)); }
      }
      return this.dataset.msgMin || this.dataset.msgNotApplicable || '[code]';
    }
    const info = this.reasonInfo(reason);
    if(info.category === 'condition' && info.tpl) { return info.tpl; }
    if(this.messageByCode[code]) { return '[code] – ' + this.messageByCode[code]; }
    return this.dataset.msgNotApplicable || '[code]';
  }
  // split a "[code]" template into a node, with the code highlighted in its own span
  _fillTemplate(parent, template, code, codeClass) {
    const parts = (template || '[code]').split('[code]');
    parent.appendChild(document.createTextNode(parts[0] || ''));
    if(parts.length > 1) {
      const span = document.createElement('span');
      span.classList.add(codeClass);
      span.textContent = code;
      parent.appendChild(span);
      parent.appendChild(document.createTextNode(parts.slice(1).join('[code]')));
    }
  }
  // build one code line: variant 'success' (green ✓) or 'warning' (amber ⓘ), with a ✕ to remove
  buildLine(code, variant, template, removeLabel) {
    const node = document.createElement('div');
    node.setAttribute('data-code', code);
    node.classList.add('active-discount', variant === 'success' ? 'discount-applicable' : 'discount-warning');
    const p = document.createElement('p');
    this._fillTemplate(p, template, code, 'active-discount__code');
    node.appendChild(p);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.classList.add('active-discount__remove');
    remove.setAttribute('aria-label', (removeLabel + ' ' + code).trim());
    remove.textContent = '✕';
    node.appendChild(remove);
    return node;
  }
  // Debounced authoritative re-read of /cart.js after a cart write — change.js payloads can carry
  // a stale applicable flag (see the theme:cart:updated listener); getCart() feeds the fresh flags
  // through captureCartTotals → signature change → reason invalidation → correct lines/bar.
  scheduleApplicabilityCheck() {
    clearTimeout(this._verifyTimer);
    this._verifyTimer = setTimeout(() => this.getCart(), 400);
  }
  // Strip confirmed-invalid (not_found) codes from the cart in a single, debounced, non-overlapping
  // cart write. Several reasons can resolve together; firing one setDiscount each makes Shopify return
  // 409 (concurrent cart update), so the strip silently fails and typos linger.
  scheduleStrip() {
    clearTimeout(this._stripTimer);
    this._stripTimer = setTimeout(() => {
      if(this._stripping || !window.cart) return;          // a cart write is already in flight
      const typos = this.discount_codes.filter(dc => !dc.applicable && this.reasonByCode[(dc.code || '').toUpperCase()] === 'not_found');
      if(!typos.length) return;
      const keep = this.discount_codes.filter(dc => typos.indexOf(dc) === -1).map(dc => dc.code);
      this._stripping = true;
      window.cart.setDiscount(keep.join(','));             // _stripping cleared on theme:cart:updated
    }, 450);
  }
  // Full pass: fetch any missing reasons, then render. Used by the authoritative getCart() path.
  init() {
    this.syncReasons();
    this.render();
  }
  // Ensure a middleware result for every code: for non-applicable codes it's the reason; for
  // applicable codes it tells us whether the code grants free shipping (Shopify doesn't expose
  // shipping-line discounts in the cart pre-checkout, so the top bar can't know otherwise).
  // This is the ONLY place reasons are fetched, so a cart change triggers exactly one sweep (after
  // the authoritative /cart.js read), not one over the stale change.js payload plus one after.
  syncReasons() {
    for(const dc of this.discount_codes) {
      const code = dc.code?.trim()?.toUpperCase();
      if(code && this.reasonByCode[code] === undefined) { this.fetchReason(code); }
    }
  }
  // An applied FS code below its minimum must show the amber warning, not a green "eingelöst" line —
  // shipping would NOT be free (Shopify keeps `applicable:true` below FS minimums; subtotal- AND
  // quantity-gated).
  isFsBelowMinimum(code) {
    const reason = this.reasonByCode[code];
    return this.fsByCode[code] && (reason === 'min_not_met' || reason === 'quantity');
  }
  render() {
    if(this.codes_wrapper) {
      const tplValid = this.dataset.msgValid || '[code]';
      const removeLabel = this.dataset.removeLabel || 'Remove';
      this.codes_wrapper.innerHTML = '';
      for(const dc of this.discount_codes) {
        const code = dc.code?.trim()?.toUpperCase();
        if(!code) continue;
        if(dc.applicable && !this.isFsBelowMinimum(code)) {
          this.codes_wrapper.appendChild(this.buildLine(code, 'success', tplValid, removeLabel));
        } else if(dc.applicable) {
          this.codes_wrapper.appendChild(this.buildLine(code, 'warning', this.conditionTemplate(code, this.reasonByCode[code]), removeLabel));
        } else {
          const reason = this.reasonByCode[code];
          if(reason === undefined) { continue; }    // classify first (syncReasons), render after
          if(reason === 'not_found') { continue; }  // typo → no line (gets stripped)
          this.codes_wrapper.appendChild(this.buildLine(code, 'warning', this.conditionTemplate(code, reason), removeLabel));
        }
      }
    }
    // remove confirmed-invalid (typo) codes from the cart — debounced into ONE cart write so
    // multiple reason responses don't fire concurrent setDiscount calls (which 409 / conflict)
    this.scheduleStrip();
    // (re)bind the remove buttons — rebuilt on every render
    this.querySelectorAll('.active-discount__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const node = btn.closest('[data-code]');
        const code = node?.dataset?.code?.trim()?.toUpperCase();
        this.removeDiscountCode(code);
      });
    });
    // amber, non-interactive condition line(s) in the price summary table(s)
    this.renderSummaryWarnings();
    // free-shipping DISCOUNT code applied → force the top free-shipping bar into its reached state
    this.updateFreeShippingBar();
  }
  // A free-shipping discount code makes shipping free without changing the subtotal, so the top
  // free-shipping bar (subtotal-driven) would keep counting down. When an applied code is a
  // free-shipping discount (per the middleware), flag the bar so CSS shows the reached state.
  // Uses its own class so the theme's subtotal-based is-success toggling doesn't fight it.
  // Shopify never re-evaluates FS minimums in the cart, so the KNOWN threshold is checked locally;
  // while a refetch is in flight (reason undefined) a known FS code keeps the bar green (no flicker).
  updateFreeShippingBar() {
    const active = this.discount_codes.some(dc => {
      if(!dc.applicable) return false;
      const code = (dc.code || '').toUpperCase();
      const reason = this.reasonByCode[code];
      const min = this.minAmountByCode[code];
      // confirmed free-shipping code (met, or no minimum)
      if(reason === 'free_shipping') return min == null || (this.cartSubtotal || 0) >= min;
      // in-flight: only keep the bar green if we already know a *subtotal* threshold that's met —
      // avoids a false green for quantity-gated FS codes (no amount) during the refetch window
      if(reason === undefined && this.fsByCode[code] && min != null) return (this.cartSubtotal || 0) >= min;
      return false;
    });
    document.querySelectorAll('[data-cart-message]').forEach(msg => msg.classList.toggle('has-fs-code', active));
    // Below the FS code's minimum, free shipping is closer than the store limit → the bar's
    // remaining amount + progress count toward the CODE's threshold (lowest one wins).
    let target = null;
    if(!active) {
      for(const dc of this.discount_codes) {
        if(!dc.applicable) continue;
        const code = (dc.code || '').toUpperCase();
        if(!this.fsByCode[code]) continue;
        const min = this.minAmountByCode[code];
        if(min != null && (this.cartSubtotal || 0) < min) { target = target == null ? min : Math.min(target, min); }
      }
    }
    this._fsBarTarget = target;
    this.applyFsBarValues();
    this._ensureFsBarObserver();
  }
  // Write the code-threshold values into the bar. theme.js's updateProgress() re-writes the
  // store-limit values asynchronously after every cart change, so a MutationObserver re-applies
  // ours whenever they get overwritten (writes are guarded to avoid observer loops).
  applyFsBarValues() {
    if(!this._fsBarTarget) return;
    const subtotal = this.cartSubtotal || 0;
    const remaining = this._fsBarTarget - subtotal;
    if(remaining <= 0) return;
    const txt = this._fmtMoneyLikeBar(remaining);
    const pct = Math.max(0, Math.min(100, Math.round((subtotal * 100) / this._fsBarTarget)));
    document.querySelectorAll('[data-cart-message]').forEach(msg => {
      msg.querySelectorAll('[data-left-to-spend]').forEach(el => {
        if(el.textContent !== txt) { el.textContent = txt; }
      });
      msg.querySelectorAll('[data-cart-message-progress]').forEach(p => {
        if(p.style.getPropertyValue('--progress-width') !== pct + '%') { p.style.setProperty('--progress-width', pct + '%'); }
        if(p.hasAttribute('value') && p.getAttribute('value') !== String(pct)) { p.setAttribute('value', String(pct)); }
      });
    });
  }
  _ensureFsBarObserver() {
    if(!this._fsBarTarget) {
      if(this._fsBarObs) { this._fsBarObs.disconnect(); this._fsBarObs = null; }
      return;
    }
    if(this._fsBarObs) return;
    // rAF-debounced: theme.js rewrites the bar after each cart change; collapse bursts and let the
    // equality guards in applyFsBarValues() prevent a write→observe→write loop.
    this._fsBarObs = new MutationObserver(() => {
      if(this._fsApplyRaf) return;
      this._fsApplyRaf = requestAnimationFrame(() => { this._fsApplyRaf = null; this.applyFsBarValues(); });
    });
    document.querySelectorAll('[data-cart-message]').forEach(msg => {
      this._fsBarObs.observe(msg, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['value', 'style'] });
    });
  }
  // Custom-element teardown: drop the observer + pending timers so drawer/page instances don't leak.
  disconnectedCallback() {
    if(this._fsBarObs) { this._fsBarObs.disconnect(); this._fsBarObs = null; }
    if(this._fsApplyRaf) { cancelAnimationFrame(this._fsApplyRaf); this._fsApplyRaf = null; }
    clearTimeout(this._verifyTimer);
    clearTimeout(this._stripTimer);
    clearTimeout(this._loadingTimer);
  }
  // Inject an amber, non-interactive line per non-applicable *condition* code into the price summary.
  // [data-cart-price-holder] is rebuilt on every cart update (then theme:cart:updated → init()), so
  // this re-injects each render. Idempotent: clears its own marker rows first. Targets every holder
  // (drawer + cart page) so both stay in sync regardless of which instance runs.
  renderSummaryWarnings() {
    document.querySelectorAll('[data-cart-price-holder]').forEach(holder => {
      const summary = holder.querySelector('.cart-drawer__summary');
      if(!summary) return;
      summary.querySelectorAll('[data-discount-warning-row]').forEach(el => el.remove());
      const total = summary.querySelector('.cart-drawer__summary-total');
      for(const dc of this.discount_codes) {
        const code = dc.code?.trim()?.toUpperCase();
        if(!code) continue;
        const reason = this.reasonByCode[code];
        // active free-shipping code → green info row ("Rabattcode X: GRATIS Versand")
        if(dc.applicable && this.fsByCode[code] && reason === 'free_shipping' && this.dataset.msgFreeShipping) {
          const min = this.minAmountByCode[code];
          if(min == null || (this.cartSubtotal || 0) >= min) {
            const row = document.createElement('div');
            row.className = 'cart-drawer__summary-line cart-drawer__summary-line--fs-success';
            row.setAttribute('data-discount-warning-row', code);
            const span = document.createElement('span');
            this._fillTemplate(span, this.dataset.msgFreeShipping, code, 'active-discount__code');
            row.appendChild(span);
            if(total) { summary.insertBefore(row, total); } else { summary.appendChild(row); }
            continue;
          }
        }
        // applied codes appear here only in the FS-below-minimum case (see render) — no spinner for them
        if(dc.applicable && !this.isFsBelowMinimum(code)) continue;
        const loading = !dc.applicable && reason === undefined;               // reason still being fetched
        if(reason === 'not_found') continue;                                  // typo → no line (gets stripped)
        if(!loading && this.reasonInfo(reason).category !== 'condition') continue; // only actionable conditions
        const row = document.createElement('div');
        row.className = 'cart-drawer__summary-line cart-drawer__summary-line--warning';
        row.setAttribute('data-discount-warning-row', code);
        if(loading) {
          row.classList.add('is-loading');
          const spinner = document.createElement('span');
          spinner.className = 'cart-drawer__summary-spinner';
          spinner.setAttribute('aria-label', this.dataset.msgLoading || 'Loading…');
          row.appendChild(spinner);
        } else {
          const span = document.createElement('span');
          this._fillTemplate(span, this.conditionTemplate(code, reason), code, 'active-discount__code');
          row.appendChild(span);
        }
        if(total) { summary.insertBefore(row, total); } else { summary.appendChild(row); }
      }
    });
  }
  connectedCallback() {
    this.input = this.querySelector('input[name="cart-discount-codes"]');
    this.button = this.querySelector('button');
    this.codes_wrapper = this.querySelector('.active-discount-codes');
    this.error_el = this.querySelector('[data-discount-error]');
    this.discount_codes = [];
    this.cart_items = [];
    this.pendingCode = null;
    this.errorCode = null;
    this.reasonByCode = {};    // CODE → middleware reason (min_not_met | quantity | needs_product | not_found | inactive | free_shipping | applicable | unknown)
    this.minAmountByCode = {}; // CODE → minimum-order threshold in cents (when the middleware knows one)
    this.fsByCode = {};        // CODE → true when the code is a free-shipping discount
    this._fsBarTarget = null;  // FS-code threshold (cents) the bar counts toward while below it
    this._fsBarObs = null;     // observer re-applying our bar values over theme.js's async writes
    this.messageByCode = {};   // CODE → optional middleware explainer text
    this._probing = {};        // CODE → true while a reason request is in flight
    this._lastSubmit = 0;
    this._stripTimer = null;
    this._stripping = false;   // true while a typo-strip cart write is in flight
    this._getCartInFlight = false;
    this._fsApplyRaf = null;   // rAF handle debouncing the FS-bar observer
    this.cartSubtotal = (window.theme && window.theme.subtotal) || 0;
    this.cartCurrency = (window.theme && window.theme.current_iso_code) || null;

    this.input?.addEventListener('keypress', e => {
      if(e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); this.submitDiscounts(); }
    });
    this.input?.addEventListener('input', () => this.clearError());
    this.button?.addEventListener('click', () => this.submitDiscounts());

    document.addEventListener('theme:cart:updated', e => {
      this.setButtonLoading(false);
      this._stripping = false; // the cart write finished → another strip may run if needed
      if(e.detail) {
        this.discount_codes = e.detail.discount_codes || [];
        if(e.detail.items) { this.cart_items = e.detail.items; }
        this.captureCartTotals(e.detail);
        // Re-render immediately from cached reasons (the price holder was just rebuilt, wiping our
        // injected rows) — but DON'T fetch here: the change.js payload can carry stale applicable
        // flags. The debounced getCart() below does the single authoritative read + fetch sweep.
        this.render();
        if(this.discount_codes.length) { this.scheduleApplicabilityCheck(); }
      }
    });
    document.addEventListener('theme:cart:added', () => this.getCart());
    this.getCart();
  }
}
if ("customElements" in window && !customElements.get("cart-discount-input")) {
  customElements.define("cart-discount-input", CartDiscountInput);
}
